import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { parseDateToLgComun } from "../../../../helpers/dateTransform";
import { useReportContext } from "../../../Report/ReportContext/useReportContext";
import { EGIconButton } from "../../../MainComponents/Button/IconButton/IconButton";
import {
  alpha,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { ReportGeneralInfo } from "../../../Report/Lists/ReportGeneralInfo";
import { useNavigate } from "react-router";
import { IndicatorsList } from "../../../MainComponents/Indicators/IndicatorsList";
import useLocalizedLabel from "../../../../hooks/useLocalizedLabel";
import useTagsCatalog from "../../../../hooks/useTagsCatalog";

export const SearchReports = ({ data, setIsFocused, handleClose }) => {
  // Custom hooks
  const [t] = useTranslation("report");
  const theme = useTheme();
  const navigate = useNavigate();
  const { icon_indicators, status_locales } = useReportContext();
  const getLocalizedLabel = useLocalizedLabel();

  // Initial states
  // Report Summary
  const [showReportSummary, setShowReportSummary] = useState(false);
  // Selected report (summary)
  const [selectedRow, setSelectedRow] = useState(null);
  // Loops through the array of tag objects and builds a new
  const transformedType = useTagsCatalog();
  // Table columns
  const columns = [
    { id: "status", label: t("ReportsList.status"), mW: 50 },
    { id: "folio", label: t("ReportsList.folio"), mW: 50 },
    { id: "subject", label: t("ReportsList.subject"), mW: 50 },
    { id: "indicators", label: t("ReportsList.indicators"), mW: 50 },
    { id: "created_at", label: t("ReportsList.created_at"), mW: 100 },
    { id: "actions", label: t("ReportsList.actions"), mW: 50 },
  ];
  // Transforms the information to display it
  const transformData = (reports) => {
    return reports.map((report) => {
      const draftEntryHistory = report?.status?.history?.find(
        (rprt) => rprt.status === "draft"
      );

      const createdAt = parseDateToLgComun(
        draftEntryHistory?.updated_at || report?.status?.assigned
      );

      return {
        id: report.id,
        status: status_locales[report.status.current].label,
        statusColor: status_locales[report.status.current].color,
        folio: report.folio,
        tracking_code: report.tracking_code,
        subject: report.subject || t("ReportsList.no_subject"),
        created_at: createdAt,
        entryway: report.entryway,
        classification: report.classification,
        indicators: report.custom_client_info.indicators,
        tags: Array.isArray(report?.custom_client_info?.tags)
          ? report.custom_client_info.tags
          : [],
      };
    });
  };
  // Table rows
  const rows = transformData(data);
  // Open report summary
  const toggleDrawer =
    (open, row = null) =>
    () => {
      setShowReportSummary(open);
      setSelectedRow(row);
    };

  return (
    <>
      {/* Report preview */}
      <ReportGeneralInfo
        showReportSummary={showReportSummary}
        setShowReportSummary={setShowReportSummary}
        setSelectedRow={setSelectedRow}
        selectedRow={selectedRow}
        toggleDrawer={toggleDrawer}
        handleClose={handleClose}
        transformedType={transformedType}
        getLocalizedLabel={getLocalizedLabel}
        icon_indicators={icon_indicators}
        translation={t}
      />
      <TableContainer
        sx={{
          px: 0.5,
          height: "100%",
          maxHeight: "71.5vh",
          overflowY: "auto",
          borderBottom: `1px solid ${theme.palette.blueGray100.main}`,
        }}
      >
        <Table stickyHeader aria-label="search reports table" size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align="left"
                  sx={{
                    minWidth: column.mW,
                    py: 1,
                    borderTop: `1px solid ${theme.palette.blueGray100.main}`,
                    backgroundColor: theme.palette.blueGray50.main,
                  }}
                >
                  <Typography variant="h6">{column.label}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell align="center">
                  <Typography
                    variant="body2"
                    fontSize={11}
                    component="fieldset"
                    sx={{
                      border: `solid 2px ${row.statusColor}`,
                      px: 0.75,
                      py: 0.4,
                      background: alpha(row.statusColor, 0.15),
                      minWidth: 80,
                    }}
                    className="rounded"
                    textAlign="center"
                  >
                    {row.status}
                  </Typography>
                </TableCell>
                <TableCell align="justify" sx={{ fontSize: 12 }}>
                  {row.folio}
                </TableCell>
                <TableCell align="justify" sx={{ fontSize: 12 }}>
                  {row.subject}
                </TableCell>
                <TableCell align="justify">
                  <IndicatorsList
                    indicators={row.indicators}
                    entryway={row.entryway}
                    classification={row.classification}
                    tags={row.tags}
                    transformedType={transformedType}
                    getLocalizedLabel={getLocalizedLabel}
                    icon_indicators={icon_indicators}
                    t={t}
                    theme={theme}
                  />
                </TableCell>
                <TableCell align="justify" sx={{ fontSize: 12 }}>
                  {row.created_at}
                </TableCell>
                <TableCell align="center">
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <EGIconButton
                      title={t("ReportsList.summary")}
                      iconName="outline_document_text"
                      iconColorHover="secondary"
                      onClick={toggleDrawer(true, row)}
                      iconSize={18}
                    />
                    <EGIconButton
                      title={t("ReportsList.view_report")}
                      iconName="outline_eye"
                      iconColor="primary"
                      onClick={() => {
                        navigate(`/reports/${row.id}`);
                        setIsFocused(false);
                      }}
                      iconSize={18}
                    />
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
